import { useEffect, useState, type ReactElement } from "react";
import {
  contentsGridFor,
  getItemDef,
  gridCapacity,
  totalWeight,
  usedCells,
  type GridState,
  type ItemInstance,
} from "@rietz/shared";
import { uiActions } from "../actions.js";
import { QuestsTab } from "../meta/quests.js";
import {
  clearDialogs,
  closeDialog,
  raiseDialog,
  setModWeapon,
  useHud,
  type DialogKind,
  type OpenContainer,
  type SearchView,
} from "../store.js";
import { FONT, PANEL_LINE, TEXT, TEXT_DIM } from "../theme.js";
import { resolveOpenBox } from "./boxLogic.js";
import type { DragState } from "./drag.js";
import { BodyDoll, EquipmentDoll } from "./equipment.js";
import { GridPanel, type GridPanelTarget } from "./grid.js";
import { HealthPane } from "./health.js";
import { findInspectItem, InspectDialog } from "./inspect.js";
import { ContextMenu } from "./menu.js";
import { ModPane } from "./modPane.js";
import { findOwnedWeapon } from "./modLogic.js";
import { Ghost, Panel } from "./panel.js";
import { formatKg, quickMove } from "./quickmove.js";
import {
  allSearched,
  beginContainerSearch,
  searchTargetId,
  syncContainerSearch,
} from "./search.js";
import { ItemTooltip } from "./tooltip.js";
import { useInventoryDrag, type TileHandlers } from "./useInventoryDrag.js";

/**
 * The inventory screen: the doll, the grids, the crate you are standing over.
 * It owns nothing beyond which panels to draw — the gesture is
 * `useInventoryDrag` (shared with the stash screen), the inventory lives in the
 * store and is replaced wholesale by `uiActions().tryOp`, rolled back by the
 * game if the server disagrees. The world stays visible behind the dim; the
 * game has not paused. Three tabs (gear, health, quests); the tab is
 * client-local and Tab always opens on the bag.
 */

type RaidTab = "gear" | "health" | "quests";

const TAB_LABELS: readonly { id: RaidTab; label: string }[] = [
  { id: "gear", label: "Gear" },
  { id: "health", label: "Health" },
  { id: "quests", label: "Quests" },
];

export function InventoryScreen(): ReactElement | null {
  const open = useHud((s) => s.invOpen);
  const inv = useHud((s) => s.inv);
  const container = useHud((s) => s.container);
  const search = useHud((s) => s.containerSearch);
  const modWeaponIid = useHud((s) => s.modWeaponIid);
  const dialogs = useHud((s) => s.dialogs);
  const profile = useHud((s) => s.profile);
  const [tab, setTab] = useState<RaidTab>("gear");

  const dnd = useInventoryDrag(inv, container);
  const { clear } = dnd;

  // Every container replacement re-derives the search view; the machine no-ops
  // when nothing drawn changed.
  useEffect(() => {
    syncContainerSearch(container);
  }, [container]);

  // A screen that closes (death, a walk away from the crate) must not leave a
  // drag armed to fire an op into the inventory it no longer shows.
  useEffect(() => {
    if (!open) {
      clear();
      // The Werkbank and the dialog stack belong to the screen: closing the bag
      // packs the lot away.
      setModWeapon(null);
      clearDialogs();
      setTab("gear");
    }
  }, [open, clear]);

  /**
   * The gun the Werkbank is open on, if it still exists — re-resolved every
   * render so the pane survives its subject vanishing, and closed in ONE place.
   * ONLY WHILE OPEN: under the menu `modWeaponIid` is the gear tab's or
   * trader's bench, on a gun this screen's `inv` could never contain.
   */
  const modWeapon =
    !open || inv === null || modWeaponIid === null
      ? null
      : findOwnedWeapon(inv, modWeaponIid, container);
  // `open` HERE TOO: while closed the subject resolves to nothing by design, and
  // that must not read as "gone".
  const modGone = open && modWeaponIid !== null && modWeapon === null;
  useEffect(() => {
    // In an effect, not inline (no store writes during render), and only for
    // the
    // subject that vanished — not a newer weapon selected since.
    if (modGone && useHud.getState().modWeaponIid === modWeaponIid) setModWeapon(null);
  }, [modGone, modWeaponIid]);

  /**
   * The floating dialogs, resolved every render on the Werkbank's contract
   * (`resolveOpenBox`, `findInspectItem`). ONLY WHILE OPEN: under the menu the
   * stack belongs to the gear and trader screens.
   */
  const resolvedDialogs = open
    ? dialogs.map((ref) => ({
        ref,
        box: ref.t === "box" && inv !== null ? resolveOpenBox(inv, container, ref.iid) : null,
        inspected:
          ref.t === "inspect" && inv !== null ? findInspectItem(inv, ref.iid, container) : null,
      }))
    : [];
  // Subjects that stopped resolving close their own dialogs. Keyed by a string
  // so the effect fires only when the set of casualties changes.
  const goneKey = resolvedDialogs
    .filter((d) => d.box === null && d.inspected === null)
    .map((d) => `${d.ref.t}:${d.ref.iid}`)
    .join(",");
  useEffect(() => {
    // In an effect, not inline: no store writes during render.
    if (goneKey === "") return;
    for (const key of goneKey.split(",")) {
      // Split on the FIRST colon: the kind is ours, the iid is the server's and
      // may contain anything.
      const idx = key.indexOf(":");
      closeDialog(key.slice(0, idx) as DialogKind, key.slice(idx + 1));
    }
  }, [goneKey]);

  if (!open || inv === null) return null;

  /**
   * Empty the crate, one item at a time, in reading order. The id list is
   * snapshotted ONCE (every transfer rebuilds the grid); an item that no longer
   * fits is skipped, not the end of the run. Unsearched items stay.
   */
  const takeAll = (): void => {
    const state = useHud.getState();
    const crate = state.container;
    if (crate === null) return;
    const targetId = searchTargetId(crate, { area: "container" });
    const view = targetId === null ? undefined : state.containerSearch[targetId];
    const hidden = view === undefined ? new Set<string>() : new Set(view.hiddenIids);
    const order = crate.grid.items
      .filter((p) => !hidden.has(p.item.iid))
      .map((p) => ({ iid: p.item.iid, x: p.x, y: p.y }))
      .sort((a, b) => (a.y === b.y ? a.x - b.x : a.y - b.y));
    for (const { iid } of order) quickMove({ t: "grid", area: "container" }, iid);
  };

  /**
   * WHICH LOOT PANEL: a body is the container that brought its owner's clothes
   * (`equipment` present — the server sends it for a corpse only). `crateView`
   * reads `undefined` as "searched", so screens that never engage the machinery
   * draw as before.
   */
  const bodyEquipment = container?.equipment;
  const crateTargetId =
    container === null || bodyEquipment !== undefined
      ? null
      : searchTargetId(container, { area: "container" });
  const crateView = crateTargetId === null ? undefined : search[crateTargetId];
  const searchDone = crateView === undefined || crateView.phase === "done";
  const covered = crateView?.phase === "covered";
  /**
   * Parts in a container the player has not been all the way through are parts
   * not found yet — for a body, all three targets.
   */
  const lootFound = allSearched(container, search);

  return (
    <div
      {...dnd.root}
      style={{
        position: "absolute",
        inset: 0,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        background: "rgba(8, 9, 11, 0.52)",
        font: FONT,
        color: TEXT,
        pointerEvents: "auto",
        userSelect: "none",
        // Keeps a drag from being reinterpreted as a scroll or a pinch by the
        // browser's touch gesture recogniser.
        touchAction: "none",
      }}
    >
      {/* The tab bar, borrowed from the menu's screen so the two read as one
          interface. A press on it must not reach the root: the root reads a
          press on the dim as "dismiss the menu", and worse, arms its own
          pointer capture. */}
      <nav
        onPointerDown={(ev) => {
          ev.stopPropagation();
        }}
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 4,
          padding: "4px 12px 0",
          borderBottom: `1px solid ${PANEL_LINE}`,
          background: "rgba(8, 9, 11, 0.45)",
          flex: "0 0 auto",
        }}
      >
        {TAB_LABELS.map((t) => (
          <button
            key={t.id}
            type="button"
            className="metatab"
            aria-selected={t.id === tab}
            onClick={() => {
              setTab(t.id);
            }}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          alignItems: "safe center",
          alignContent: "safe center",
          justifyContent: "safe center",
          flexWrap: "wrap",
          gap: 14,
          padding: 16,
          boxSizing: "border-box",
          overflowX: "hidden",
          overflowY: "auto",
        }}
      >
        {tab === "health" ? (
          <Panel
            title="Health"
            hint="Tab"
            onClose={() => {
              uiActions().setInventoryOpen(false);
            }}
          >
            <HealthPane />
          </Panel>
        ) : tab === "quests" ? (
          <Panel
            title="Quests"
            hint="Tab"
            onClose={() => {
              uiActions().setInventoryOpen(false);
            }}
          >
            {/* The log is the MENU's, unchanged: the profile it reads survives
                into the raid (`profile` is not part of the raid slice), and
                progress is credited at settlement, so what it shows in here is
                what was true at deployment — which is exactly the list a player
                wants to re-read. A direct server has no profile and no quests. */}
            {/* Logical pixels, not vw/vh: `#ui` is scaled (see `viewportToUi`),
                so viewport units would overshoot the root by that factor.
                760 × 480 fits a 1280-wide window at the current 150%. */}
            <div style={{ width: 760, height: 480 }}>
              {profile === null ? (
                <span style={{ color: TEXT_DIM }}>No quests on this server.</span>
              ) : (
                // `handover={false}`: the trade-in counter belongs to the menu;
                // the profile in here is the one the player deployed with.
                <QuestsTab
                  quests={profile.quests}
                  stash={profile.stash}
                  kit={profile.kit}
                  handover={false}
                />
              )}
            </div>
          </Panel>
        ) : (
          <>
          <Panel
            title="Gear"
            hint="Tab"
            header={<span style={{ fontSize: 11, color: TEXT_DIM }}>{formatKg(totalWeight(inv))}</span>}
            onClose={() => {
              uiActions().setInventoryOpen(false);
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 16,
                alignItems: "flex-start",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <EquipmentDoll inv={inv} drag={dnd.drag} {...dnd.tiles} />
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <GridPanel
                  title="Pockets"
                  area="pockets"
                  grid={inv.pockets}
                  drag={dnd.drag}
                  {...dnd.tiles}
                />
                {inv.rig === null ? null : (
                  <GridPanel title="Tactical rig" area="rig" grid={inv.rig} drag={dnd.drag} {...dnd.tiles} />
                )}
                {inv.backpack === null ? null : (
                  <GridPanel
                    title="Backpack"
                    area="backpack"
                    grid={inv.backpack}
                    drag={dnd.drag}
                    {...dnd.tiles}
                  />
                )}
              </div>
            </div>
          </Panel>

          {/* The Werkbank takes the second column, and the loot panel stands down
              while it is up: two panels of grids plus a bench plus a turntable does
              not fit a 1280-wide window, and the crate's contents are not lost —
              parts lying in a SEARCHED one are still listed as compatible, labelled
              "Container". Closing the bench brings the crate straight back. */}
          {modWeapon !== null ? (
            <Panel
              title={`Werkbank · ${modWeapon.def.name}`}
              onClose={() => {
                setModWeapon(null);
              }}
            >
              <ModPane
                inv={inv}
                // An unsearched grid's parts would leak what the cover hides.
                container={lootFound ? container : null}
                weapon={modWeapon}
                drag={dnd.drag}
                onTilePointerDown={dnd.tiles.onTilePointerDown}
                onTileContextMenu={dnd.tiles.onTileContextMenu}
              />
            </Panel>
          ) : container === null ? null : bodyEquipment !== undefined ? (
            /* A BODY. The doll shows what he was wearing AT ONCE — Tarkov never
               made you rummage for a man's helmet, it is on his head — and the
               three grids beside it are each rummaged on their own: his
               pockets, his rig, his pack. No fill readout and no "Take all":
               emptying a corpse in one click is a looting simulator, and the
               header would be counting three grids the player has not
               necessarily looked in yet. */
            <Panel
              title={container.name}
              onClose={() => {
                uiActions().closeContainer();
              }}
            >
              <div
                style={{
                  display: "flex",
                  gap: 16,
                  alignItems: "flex-start",
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                <BodyDoll equipment={bodyEquipment} drag={dnd.drag} {...dnd.tiles} />
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <LootGrid
                    caption="Pockets"
                    target={{ area: "container" }}
                    grid={container.grid}
                    targetId={searchTargetId(container, { area: "container" })}
                    views={search}
                    drag={dnd.drag}
                    tiles={dnd.tiles}
                  />
                  {bodyBags(bodyEquipment).map((bag) => (
                    <LootGrid
                      key={bag.iid}
                      caption={bag.caption}
                      target={{ area: "inside", boxIid: bag.iid }}
                      grid={bag.grid}
                      targetId={searchTargetId(container, { area: "inside", boxIid: bag.iid })}
                      views={search}
                      drag={dnd.drag}
                      tiles={dnd.tiles}
                    />
                  ))}
                </div>
              </div>
            </Panel>
          ) : (
            <Panel
              title={container.name}
              header={
                <>
                  <span style={{ fontSize: 11, color: TEXT_DIM }}>
                    {/* How full an unsearched crate is would give away how much is
                        in it, which is the one thing the cover exists to withhold. */}
                    {searchDone ? usedCells(container.grid) : "?"}/
                    {gridCapacity(container.grid)}
                  </span>
                  {covered ? null : (
                    <button type="button" className="invtake" onClick={takeAll}>
                      Take all
                    </button>
                  )}
                </>
              }
              onClose={() => {
                uiActions().closeContainer();
              }}
            >
              <LootGrid
                caption={null}
                target={{ area: "container" }}
                grid={container.grid}
                targetId={crateTargetId}
                views={search}
                drag={dnd.drag}
                tiles={dnd.tiles}
              />
            </Panel>
          )}

          {/* The dialogs: opened bags/boxes and item descriptions, floating over
              the inventory. Each remains only as large as its own panel, so the
              grids behind them stay reachable for dragging items into and out of
              the open container, and a player can keep sorting while a
              description is up.

              ONE LIST, the two kinds interleaved, because the ORDER IS THE
              STACKING (see `dialogs`): splitting them into two blocks would pin
              every description above every box forever, and the whole point of
              letting them co-exist is being able to put the one you are working
              in on top. `nudge` staggers where each window first appears so the
              second one is not hidden exactly behind the first. */}
          {resolvedDialogs.map(({ ref, box, inspected }, i) => {
            const key = `${ref.t}:${ref.iid}`;
            const nudge = i * 26;
            const raise = () => raiseDialog(ref.t, ref.iid);
            if (ref.t === "box") {
              return box === null ? null : (
                <Panel
                  overlay
                  key={key}
                  nudge={nudge}
                  onRaise={raise}
                  title={box.def.name}
                  header={
                    <span style={{ fontSize: 11, color: TEXT_DIM }}>
                      {box.fill.used}/{box.fill.total}
                    </span>
                  }
                  onClose={() => {
                    closeDialog("box", ref.iid);
                  }}
                >
                  <GridPanel
                    title=""
                    {...box.target}
                    grid={box.grid}
                    drag={dnd.drag}
                    {...dnd.tiles}
                  />
                </Panel>
              );
            }
            return inspected === null ? null : (
              <InspectDialog
                key={key}
                nudge={nudge}
                onRaise={raise}
                subject={inspected}
                onClose={() => {
                  closeDialog("inspect", ref.iid);
                }}
              />
            );
          })}
          </>
        )}
      </div>

      <Ghost drag={dnd.drag} inv={inv} container={container} />

      {dnd.tip === null ? null : (
        <ItemTooltip tip={dnd.tip} inv={inv} container={container} />
      )}

      {dnd.menu === null ? null : (
        <ContextMenu
          menu={dnd.menu}
          inv={inv}
          container={container}
          onClose={dnd.closeMenu}
        />
      )}
    </div>
  );
}

/**
 * The dead man's bags, in doll order — looted THROUGH the corpse. A worn bag
 * arrives WRAPPED (`contents` on the instance), so the grid is
 * `contentsGridFor` and the address `{ area: "inside", iid }`. An unresolvable
 * slot is skipped.
 */
function bodyBags(
  equipment: NonNullable<OpenContainer["equipment"]>,
): { iid: string; caption: string; grid: GridState }[] {
  const out: { iid: string; caption: string; grid: GridState }[] = [];
  for (const { slot, label } of [
    { slot: "rig", label: "Tactical rig" },
    { slot: "backpack", label: "Backpack" },
  ] as const) {
    const bag: ItemInstance | null = equipment[slot];
    if (bag === null) continue;
    const def = getItemDef(bag.defId);
    if (def === null || def.kind !== "container") continue;
    out.push({ iid: bag.iid, caption: `${label} · ${def.name}`, grid: contentsGridFor(def, bag) });
  }
  return out;
}

/**
 * One rummageable grid: the lattice, and the cover over it until searched. ONE
 * component for a crate's grid and each of a body's three. The cover is a
 * SIBLING of the grid, outside `[data-drop-area]`, so `zoneAt` misses the grid
 * and nothing can be dropped into something unsearched (`computeHover` says the
 * same out loud — `DropLock`). `caption` is `null` for the crate; a body's
 * grids count nothing until searched.
 */
function LootGrid(props: {
  caption: string | null;
  target: GridPanelTarget;
  grid: GridState;
  /** `null` for a grid that is nobody's target — never covered, never hidden. */
  targetId: string | null;
  views: Readonly<Record<string, SearchView>>;
  drag: DragState | null;
  tiles: TileHandlers;
}): ReactElement {
  const view = props.targetId === null ? undefined : props.views[props.targetId];
  const done = view === undefined || view.phase === "done";
  const covered = view?.phase === "covered";
  const targetId = props.targetId;

  const lattice = (
    /* `fit-content` so the wrapper is exactly the grid and the cover's
       `inset: 0` lands on it — the panel itself is as wide as its header, which
       for a small crate is wider than the lattice. */
    <div style={{ position: "relative", width: "fit-content" }}>
      <GridPanel
        title=""
        {...props.target}
        grid={props.grid}
        drag={props.drag}
        hiddenIids={view !== undefined && !done ? new Set(view.hiddenIids) : undefined}
        searchFocusIid={done ? null : view?.focusIid ?? null}
        {...props.tiles}
      />
      {!covered || targetId === null ? null : (
        <div
          className="invsearch-cover"
          onClick={() => {
            beginContainerSearch(targetId);
          }}
        >
          <div className="invsearch-glass" />
          <div className="invsearch-cover-title">Unsearched</div>
          <div className="invsearch-cover-hint">Click to search</div>
        </div>
      )}
    </div>
  );

  if (props.caption === null) return lattice;
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 4,
          color: TEXT_DIM,
          fontSize: 11,
          letterSpacing: "0.04em",
        }}
      >
        <span>{props.caption}</span>
        <span>
          {done ? usedCells(props.grid) : "?"}/{gridCapacity(props.grid)}
        </span>
      </div>
      {lattice}
    </div>
  );
}
